import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ENVIRONMENT } from '@ninety/mobile/platform/environments';

import { MilestoneModel } from '../_state/models/milestone.model';
import { MilestonesResponse } from '../_state/models/rocks-milestones-response.model';

/** Query args for the paged milestones-for-user list. */
export interface MilestoneListParams {
  sortDirection: string;
  dueDateRange: { startDate: Date | null; endDate: Date | null };
  status?: string;
  filterFutureCurrent?: boolean;
}

/**
 * HTTP client for the Milestones backend. One method per controller endpoint; owns URL composition,
 * query-param building, and verbs. Every operation targets the nest service — the
 * `MilestonesApiFlag` that used to pick monolith-or-nest per operation is gone, along with the
 * monolith routes it could select.
 */
@Injectable({ providedIn: 'root' })
export class MilestonesApiService {
  private readonly http = inject(HttpClient);
  private readonly origin = inject(ENVIRONMENT).origin;

  private readonly nest = `${this.origin.milestonesNest}/v1/milestones`;

  getForUser(params: MilestoneListParams): Observable<MilestonesResponse> {
    return this.http.get<MilestonesResponse>(`${this.nest}/user`, { params: this.buildListParams(params) });
  }

  getById(id: string): Observable<MilestoneModel> {
    return this.http.get<MilestoneModel>(`${this.nest}/${id}`);
  }

  create(milestone: Partial<MilestoneModel>): Observable<MilestoneModel> {
    return this.http.post<MilestoneModel>(this.nest, milestone);
  }

  update(id: string, changes: Partial<MilestoneModel>): Observable<MilestoneModel> {
    return this.http.patch<MilestoneModel>(`${this.nest}/${id}`, changes);
  }

  /** Soft-delete rides the `update` route (there is no dedicated delete op). */
  delete(id: string): Observable<MilestoneModel> {
    return this.http.patch<MilestoneModel>(`${this.nest}/${id}`, { isDeleted: true });
  }

  private buildListParams(o: MilestoneListParams): HttpParams {
    let params = new HttpParams()
      .set('statusCode', o.status?.toLowerCase() ?? '')
      .set('sortField', 'dueDate')
      .set('sortDirection', o.sortDirection);

    if (o.dueDateRange.startDate) {
      params = params.set('startDateFilter', o.dueDateRange.startDate.toISOString());
    }
    if (o.dueDateRange.endDate) {
      params = params.set('endDateFilter', o.dueDateRange.endDate.toISOString());
    }
    if (o.filterFutureCurrent) {
      params = params.set('filterFutureCurrent', 'true');
    }

    return params;
  }
}
